import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Stack, SvgIcon, TextField, useMediaQuery, useTheme } from '@mui/material';
import { Plus, SearchSm } from '../../../assets';
import useLogError from '../../../hooks/useLogError';
import { SuccessResponse_1OfPagedData_1OfUser, User } from '../../../app/services/api/generated';
import useNswagClient from '../../../hooks/api/useNswagClient';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import { Section } from '../../../components/ui/Section';
import UsersTable from './components/UsersTable';
import { UpsertUserRequest } from './types/UpsertUserRequest';
import CreateUpdateUserModal from './components/CreateUpdateUserModal';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { UserContext } from '../../../components/shared/useUser';

const ROWS_PER_PAGE = 10;

const ManageUsers = () => {
  const { searchUsers, createUser } = useNswagClient();
  const { t } = useTranslation('manageUsers');
  const [users, setUsers] = useState<User[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { logError } = useLogError();
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('emailAddress');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showCreateSuccessMessage, setShowCreateSuccessMessage] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { hasPermissionTo } = useContext(UserContext);

  // Track applied filters separately from input fields
  const [appliedFilters, setAppliedFilters] = useState({
    emailAddress: '',
    accessScope: '',
    role: '',
  });

  const isInitialMount = useRef(true);

  const loadData = useCallback((
    currentPage: number,
    currentSortBy: string,
    currentSortDirection: string,
    filters: { emailAddress: string; accessScope: string; role: string },
  ) => {
    setIsLoading(true);

    const skip = currentPage * ROWS_PER_PAGE;

    searchUsers(
      skip,
      ROWS_PER_PAGE,
      filters.emailAddress || undefined,
      filters.accessScope || undefined,
      filters.role || undefined,
      currentSortBy,
      currentSortDirection,
    )
      .then((result: SuccessResponse_1OfPagedData_1OfUser) => {
        if (result?.data) {
          setUsers(result.data.data ?? []);
          setTotalCount(result.data.totalCount ?? 0);
        }
      })
      .catch((error) => logError(error))
      .finally(() => setIsLoading(false));
  }, [searchUsers, logError]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      loadData(0, sortBy, sortDirection, appliedFilters);
    }
  }, []);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadData(newPage, sortBy, sortDirection, appliedFilters);
  };

  const handleSortChange = (newSortBy: string, newSortDirection: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
    setPage(0);
    loadData(0, newSortBy, newSortDirection, appliedFilters);
  };

  const handleSearch = () => {
    const newFilters = {
      emailAddress: searchTerm,
      accessScope: companyFilter,
      role: roleFilter,
    };
    setAppliedFilters(newFilters);
    setPage(0);
    loadData(0, sortBy, sortDirection, newFilters);
  };

  const handleClear = () => {
    setSearchTerm('');
    setCompanyFilter('');
    setRoleFilter('');
    const emptyFilters = { emailAddress: '', accessScope: '', role: '' };
    setAppliedFilters(emptyFilters);
    setPage(0);
    loadData(0, sortBy, sortDirection, emptyFilters);
  };

  const handleCreate = (body: UpsertUserRequest) => {
    createUser(body)
      .then(() => {
        setShowCreateSuccessMessage(true);
        setPage(0);
        loadData(0, sortBy, sortDirection, appliedFilters);
      })
      .catch(error => logError(error));
  };

  const refreshCurrentPage = () => {
    loadData(page, sortBy, sortDirection, appliedFilters);
  };

  const maxPages = Math.max(1, Math.ceil(totalCount / ROWS_PER_PAGE));

  return (
    <PageArea>
      <Box
        display='flex'
        width='100%'
        height='100%'
        flexDirection='column'
      >
        <Grid container>
          <Stack
            width='100%'
            spacing={2}
            direction={isMobile ? 'column' : 'row'}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Box width={isMobile ? '100%' : 'auto'}>
              <PageTitle subTitle={t('manageUsers')}>{t('users')}</PageTitle>
            </Box>
            <Box width={isMobile ? '100%' : 'auto'}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              <Button
                variant="primary"
                size="lg"
                sx={{
                  mb: { xs: 4, md: 0 },
                }}
                disabled={!hasPermissionTo(['UserManagementCreate'])}
                onClick={() => setShowCreateModal(true)}
                startIcon={<SvgIcon><Plus fill="white" /></SvgIcon>}
              >
                {t('newUser')}
              </Button>
            </Box>
          </Stack>

          <Grid container
            spacing={2}>
            <Grid container
              sx={{ pt: '6px', pb: '12px' }}
              spacing={4}>
              <Grid item
                lg={2}
                sm={4}
                xs={12}>
                <TextField
                  fullWidth
                  value={searchTerm}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  size='small'
                  label={t('email')}
                />
              </Grid>
              <Grid item
                lg={2}
                sm={4}
                xs={12}>
                <TextField
                  fullWidth
                  value={companyFilter}
                  onChange={(event) => setCompanyFilter(event.target.value)}
                  size='small'
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  label={t('accessScope')}
                />
              </Grid>
              <Grid item
                lg={2}
                sm={4}
                xs={12}>
                <TextField
                  fullWidth
                  value={roleFilter}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  onChange={(event) => setRoleFilter(event.target.value)}
                  size='small'
                  label={t('role')}
                />
              </Grid>

              <Grid item
                xs={12}
                sm={2}
                lg={2}
                sx={{ maxWidth: 'fit-content', mr: 2 }}>
                <Button
                  fullWidth
                  variant="secondary"
                  size="lg"
                  onClick={() => handleClear()}
                >
                  {t('clearFilters')}
                </Button>
              </Grid>
              <Grid item
                lg={2}
                sm={2}
                xs={12}
                sx={{ maxWidth: 'fit-content' }}>
                <Button
                  fullWidth
                  variant="primary"
                  size="lg"
                  onClick={handleSearch}
                  startIcon={<SvgIcon> <SearchSm /> </SvgIcon>}
                >
                  {t('search')}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Grid item
            xs={12}
            sx={{ mt: '12px' }}>
            {users &&
              <Section>
                <UsersTable
                  list={users}
                  page={page}
                  maxPages={maxPages}
                  setPage={handlePageChange}
                  clearAll={handleClear}
                  searchTerm=''
                  loadData={refreshCurrentPage}
                  isLoading={isLoading}
                  onCreate={() => setShowCreateModal(true)}
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSortChange={handleSortChange}
                />
              </Section>
            }
          </Grid>
        </Grid>
        <CreateUpdateUserModal
          openModal={showCreateModal}
          onConfirm={handleCreate}
          editUser={undefined}
          onCancel={() => setShowCreateModal(false)}
          onResetPassword={() => { }}
        />
        <MessagesSnackbar
          open={showCreateSuccessMessage}
          onClose={() => setShowCreateSuccessMessage(false)}
          message={t('successCreateMessage')}
          severity="success"
          duration={null}
        />
      </Box>
    </PageArea>
  );
};

export default ManageUsers;